import { useState } from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PhotoCard — renders one photo with:
 *   • Visible source credit ribbon (always visible, not just on hover)
 *   • Hover overlay with caption + "Request takedown" button
 *   • Self-contained takedown dialog
 */
export function PhotoCard({ photo, subjectId, subjectKind = "depot", onClick, canDelete, onDelete }) {
  const [openTakedown, setOpenTakedown] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const credit = photo.credit || (photo.uploaded_by ? `Submitted by ${photo.uploaded_by}` : null);
  const isExternal = ["depotmaps.com", "northamericaninterlockings.com", "Wikipedia"].some((s) =>
    (credit || "").toLowerCase().includes(s.toLowerCase())
  );

  const src = photo.image_data?.startsWith("data:")
    ? photo.image_data
    : `data:image/jpeg;base64,${photo.image_data}`;

  return (
    <div
      className="relative aspect-square rounded-lg overflow-hidden group border border-white/5"
      data-testid={`photo-${photo.id}`}
    >
      {/* image */}
      <img
        src={src}
        alt={photo.caption || "Photo"}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-zoom-in"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(photo);
        }}
      />

      {/* Always-visible credit ribbon at bottom — does NOT depend on hover */}
      <div
        className="absolute left-0 right-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/90 to-transparent pointer-events-none"
        data-testid={`photo-credit-${photo.id}`}
      >
        <p className="text-[10px] text-white/80 font-mono leading-tight truncate">
          {credit || "Source: Unknown"}
          {isExternal && <span className="text-amber-400/80 ml-1">· archive</span>}
        </p>
      </div>

      {/* Hover overlay with caption + takedown */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3 pointer-events-none">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenTakedown(true);
          }}
          className="self-end pointer-events-auto bg-black/70 hover:bg-red-600/80 text-white text-[10px] uppercase tracking-wider font-bold rounded-full px-2.5 py-1 flex items-center gap-1 border border-white/20 transition-colors"
          data-testid={`takedown-btn-${photo.id}`}
          title="Request removal of this photo"
        >
          <AlertTriangle className="w-3 h-3" />
          Report
        </button>
        {canDelete && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (!window.confirm("Delete this photo permanently?")) return;
              try {
                setDeleting(true);
                await axios.delete(`${API}/admin/photos/${photo.id}`, { withCredentials: true });
                toast.success("Photo deleted");
                onDelete?.(photo.id);
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to delete");
              } finally {
                setDeleting(false);
              }
            }}
            disabled={deleting}
            className="self-end pointer-events-auto bg-black/70 hover:bg-red-700/90 text-white text-[10px] uppercase tracking-wider font-bold rounded-full px-2.5 py-1 flex items-center gap-1 border border-red-500/40 transition-colors"
            data-testid={`admin-delete-photo-${photo.id}`}
            title="Admin: delete this photo"
          >
            <Trash2 className="w-3 h-3" />
            {deleting ? "…" : "Delete"}
          </button>
        )}
        <div className="text-white pointer-events-none">
          {photo.caption && (
            <p className="text-sm font-medium drop-shadow-sm" data-testid={`caption-${photo.id}`}>
              {photo.caption}
            </p>
          )}
        </div>
      </div>

      <TakedownDialog
        open={openTakedown}
        onOpenChange={setOpenTakedown}
        photo={photo}
        subjectId={subjectId}
        subjectKind={subjectKind}
      />
    </div>
  );
}

function TakedownDialog({ open, onOpenChange, photo, subjectId, subjectKind }) {
  const [form, setForm] = useState({
    contact_email: "",
    requester_name: "",
    relationship_to_work: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_email.includes("@") || form.reason.trim().length < 10) {
      toast.error("Please provide a valid email and a reason (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        photo_id: photo.id,
        depot_or_tower_id: subjectId,
        ...form,
      };
      const res = await axios.post(`${API}/takedown`, payload);
      toast.success(res.data.message || "Request submitted. We'll respond within 48 hours.");
      onOpenChange(false);
      setForm({ contact_email: "", requester_name: "", relationship_to_work: "", reason: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request. Please email takedown@railstream.net directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#111] border-white/10 text-white" data-testid="takedown-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Request Photo Removal
          </DialogTitle>
          <DialogDescription className="text-white/60">
            This is a {subjectKind === "tower" ? "tower" : "depot"} photo. If you own the rights and want it
            removed or recredited, fill this out and we'll respond within 48 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="text-xs bg-white/5 rounded-md p-3 text-white/60 flex gap-2">
            <Info className="w-4 h-4 shrink-0 text-amber-500 mt-0.5" />
            <span>
              You can also email <a className="text-amber-400 underline" href="mailto:takedown@railstream.net">takedown@railstream.net</a> directly.
            </span>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Your email *</label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="you@example.com"
              className="bg-white/5 border-white/10 text-white"
              data-testid="takedown-email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Your name</label>
            <Input
              value={form.requester_name}
              onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
              placeholder="Optional"
              className="bg-white/5 border-white/10 text-white"
              data-testid="takedown-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Relationship to the work</label>
            <Input
              value={form.relationship_to_work}
              onChange={(e) => setForm({ ...form, relationship_to_work: e.target.value })}
              placeholder="e.g. Photographer, copyright owner, estate representative"
              className="bg-white/5 border-white/10 text-white"
              data-testid="takedown-relationship"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1 block">Reason / details *</label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Explain why this should be removed or recredited (min 10 chars)."
              className="bg-white/5 border-white/10 text-white h-24"
              data-testid="takedown-reason"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="takedown-submit"
            >
              {submitting ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoCard;
